import {
  adminV1OrganizationsPaymentsInvoicesList,
  adminV1OrganizationsPaymentsInvoicesCreate,
  adminV1OrganizationsPaymentsInvoicesRetrieve,
  adminV1OrganizationsPaymentsInvoicesUpdate,
  adminV1OrganizationsPaymentsInvoicesPayWithCashCreate,
} from "@/api/admin-payments-invoices/admin-payments-invoices";

import {
  adminV1OrganizationsPaymentsLogList,
  adminV1OrganizationsPaymentsLogRetrieve,
} from "@/api/admin-paymentslog/admin-paymentslog";

// Raw passthroughs for newer scheduled-payment endpoints. Composables in this
// module use these so the module has a single import surface for Orval clients.
// The composables keep their own org scoping and Sentry error handling, so we
// do not wrap them in the {result|error} shape used by the older helpers below.
export {
  adminV1OrganizationsPaymentsScheduledPaymentsAmendCreate,
  adminV1OrganizationsPaymentsScheduledPaymentsRetrieve,
} from "@/api/admin-payments-scheduled-payments/admin-payments-scheduled-payments";

import type {
  AdminV1OrganizationsPaymentsLogListParams,
  PaginatedPaymentsLogList,
  PaymentsLog,
  PaymentsLogDetail,
  AdminV1OrganizationsPaymentsInvoicesListParams,
  PaginatedInvoiceListList,
  CreateInvoice,
  UpdateInvoice,
} from "@/api/model";
import { useCurrentOrganization } from "@/composables/useCurrentOrganization";
import customInstance from "@/shared/services/axios-client";

function getDefaultOrganizationId(): number {
  const { currentOrganizationId } = useCurrentOrganization();
  const orgId = currentOrganizationId.value;
  if (!orgId) {
    throw new Error("No default organization found");
  }
  return orgId;
}

type ApiError = {
  error: string;
};

export const getPaymentsLog = async (
  params: AdminV1OrganizationsPaymentsLogListParams
): Promise<PaginatedPaymentsLogList | ApiError> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const results = await adminV1OrganizationsPaymentsLogList(
      organizationId,
      params
    );

    return results;
  } catch (e: any) {
    return { error: e.message };
  }
};

// The list endpoint doubles as the CSV export when ?format=csv is set. Orval
// types the function as JSON-only because the OpenAPI declares the 200 as a
// paginated JSON page; we call the same axios mutator directly with
// responseType: "blob" so the binary CSV bytes are returned instead of parsed
// JSON.
//
// Unlike getPaymentsLog above, this deliberately does NOT catch and return an
// ApiError shape: the caller's try/catch drives the failure toast, and
// downloadBlob has nothing useful to do with an error object.
export const getPaymentsLogCsv = (
  params: AdminV1OrganizationsPaymentsLogListParams
): Promise<Blob> => {
  const organizationId = getDefaultOrganizationId();
  return customInstance<Blob>({
    url: `/api/admin/v1/organizations/${organizationId}/payments/log/`,
    method: "GET",
    params: { ...params, format: "csv" },
    responseType: "blob",
  });
};

export const getPaymentDetail = async (
  paymentId: string
): Promise<PaymentsLogDetail | ApiError> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsPaymentsLogRetrieve(
      organizationId,
      paymentId
    );
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const getInvoices = async (
  params: AdminV1OrganizationsPaymentsInvoicesListParams
): Promise<PaginatedInvoiceListList | ApiError> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const results = await adminV1OrganizationsPaymentsInvoicesList(
      organizationId,
      params
    );

    return results;
  } catch (e: unknown) {
    return { error: e instanceof Error ? e.message : "Unknown error" };
  }
};

export const getInvoice = async (id: string) => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsPaymentsInvoicesRetrieve(
      organizationId,
      id
    );
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const createInvoice = async (
  params: CreateInvoice
): Promise<CreateInvoice | ApiError> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsPaymentsInvoicesCreate(
      organizationId,
      params
    );
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const updateInvoice = async (
  id: string,
  params: UpdateInvoice
): Promise<UpdateInvoice | ApiError> => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsPaymentsInvoicesUpdate(
      organizationId,
      id,
      params
    );
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const markInvoiceAsPaid = async (id: string) => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsPaymentsInvoicesPayWithCashCreate(
      organizationId,
      id
    );
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};
